
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Icon from '../../components/Icon';
import { getAppHeaderNavItems } from './appNavigation';

export default function AppHeader({
  publicLogo,
  belRoboticsLogo,
  husqvarnaLogo,
  kressLogo,
  navigateAndLog,
  meldingenBadgeCount,
  notificationCount,
  showNotificationPopup,
  onToggleNotificationPopup,
  voiceSystem,
  startVoiceRecognition,
  stopVoiceRecognition,
  darkMode,
  onToggleDarkMode,
  onLogout,
  userProfile,
  user,
  navigationContext,
}) {
  const location = useLocation();
  const currentPath = location.pathname;
  const currentSearch = location.search || '';
  const visibleNotificationCount = notificationCount > 99 ? '99+' : notificationCount;
  const headerRef = useRef(null);
  const mobileMenuTriggerRef = useRef(null);
  const mobileAccountTriggerRef = useRef(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobilePanel, setMobilePanel] = useState(null);
  const previousMobilePanelRef = useRef(null);
  const navItems = getAppHeaderNavItems({
    ...(navigationContext || {}),
    locationPath: currentPath,
    meldingenBadgeCount,
  });
  const activeNavKey = navItems.find((item) => item.active)?.key || null;

  const closeAllDropdowns = () => setOpenDropdown(null);
  const toggleDropdown = (key) => {
    setOpenDropdown((current) => (current === key ? null : key));
  };

  useEffect(() => {
    closeAllDropdowns();
    setMobilePanel(null);
  }, [currentPath, currentSearch]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[route-state]', {
        pathname: currentPath,
        openDropdown,
        activeNavKey,
        renderedRouteKey: `${currentPath}${currentSearch}`,
      });
    }
  }, [activeNavKey, currentPath, currentSearch, openDropdown]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!headerRef.current || headerRef.current.contains(event.target)) return;
      closeAllDropdowns();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeAllDropdowns();
        setMobilePanel(null);
      }
    };
    const onScrollOrWheel = () => {
      closeAllDropdowns();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScrollOrWheel, true);
    window.addEventListener('wheel', onScrollOrWheel, { passive: true });
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScrollOrWheel, true);
      window.removeEventListener('wheel', onScrollOrWheel);
    };
  }, []);

  useEffect(() => {
    const isMobilePanelOpen = mobilePanel !== null;
    document.body.classList.toggle('mobile-panel-open', isMobilePanelOpen);
    if (!isMobilePanelOpen) {
      const previousPanel = previousMobilePanelRef.current;
      (previousPanel === 'account' ? mobileAccountTriggerRef : mobileMenuTriggerRef).current?.focus();
      return undefined;
    }
    previousMobilePanelRef.current = mobilePanel;
    return () => document.body.classList.remove('mobile-panel-open');
  }, [mobilePanel]);

  const handleNavClick = () => {
    closeAllDropdowns();
  };

  const handleSubmenuClick = () => {
    closeAllDropdowns();
    setMobilePanel(null);
  };

  const mobileNavItems = navItems.flatMap((item) => [
    { key: item.key, label: item.label, path: item.path, active: item.active, badgeCount: item.badgeCount },
    ...item.submenu.map((subitem) => ({
      ...subitem,
      active: currentPath === subitem.path,
      parentLabel: item.label,
    })),
  ]);
  const currentPageLabel = mobileNavItems.find((item) => item.active)?.label || 'Robot Beheer';

  const handleNavTriggerClick = (item, hasSubmenu) => {
    if (hasSubmenu) {
      toggleDropdown(item.key);
      return;
    }
    navigateAndLog(item.path);
  };

  const toggleMobilePanel = (panel) => {
    setMobilePanel((current) => (current === panel ? null : panel));
  };

  const renderDesktopNavItem = (item) => {
    const hasSubmenu = Array.isArray(item.submenu) && item.submenu.length > 0;
    return (
      <div
        key={item.key}
        className={`nav-item-wrap ${hasSubmenu ? 'has-submenu' : ''} ${item.key === 'administratie' ? 'nav-item-wrap--administratie' : ''} ${openDropdown === item.key ? 'is-open' : ''}`}
      >
        {hasSubmenu ? (
          <div className="nav-item-with-menu">
            <NavLink
              to={item.path}
              className={`nav-item ${item.active ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <span className="nav-item-label">{item.label}</span>
              {item.badgeCount > 0 && <span className="nav-badge" title="Nieuwe, ongelezen meldingsinformatie in deze vestiging" aria-label={item.badgeCount + ' ongelezen meldingen'}>{item.badgeCount}</span>}
            </NavLink>
            <button
              type="button"
              className={`nav-item-menu-toggle ${item.active ? 'active' : ''}`}
              onClick={() => handleNavTriggerClick(item, hasSubmenu)}
              aria-haspopup="menu"
              aria-expanded={openDropdown === item.key}
              aria-label={`${item.label} menu openen`}
            >
              <span className="nav-item-caret" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <NavLink
            to={item.path}
            className={`nav-item ${item.active ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <span className="nav-item-label">{item.label}</span>
            {item.badgeCount > 0 && <span className="nav-badge" title="Nieuwe, ongelezen meldingsinformatie in deze vestiging" aria-label={item.badgeCount + ' ongelezen meldingen'}>{item.badgeCount}</span>}
          </NavLink>
        )}

        {hasSubmenu && (
          <div
            className={`nav-dropdown ${item.key === 'administratie' ? 'nav-dropdown--administratie' : ''} ${openDropdown === item.key ? 'is-open' : ''}`}
            role="menu"
            aria-label={`${item.label} opties`}
          >
            <div className="nav-dropdown-title">{item.label}</div>
            {item.submenu.map((subitem) => (
              <NavLink
                key={subitem.key}
                to={subitem.path}
                className={`nav-dropdown-item ${currentPath === subitem.path ? 'active' : ''}`}
                onClick={handleSubmenuClick}
                role="menuitem"
                aria-current={currentPath === subitem.path ? 'page' : undefined}
              >
                {subitem.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  const navigationGroups = [
    { key: 'workspace', items: navItems.filter((item) => item.group === 'workspace') },
    { key: 'operations', items: navItems.filter((item) => item.group === 'operations') },
    { key: 'account', items: navItems.filter((item) => item.group === 'account') },
  ].filter((group) => group.items.length > 0);

  return (
    <header className="app-header" ref={headerRef}>
      <div className="header-logo">
        <img src={publicLogo} alt="Krinkels" className="krinkels-main-logo" />
        <div className="header-brand-copy" aria-label="Robotbeheer">
          <span>Krinkels</span>
          <strong>Robotbeheer</strong>
        </div>
      </div>

      <div className="mobile-header-bar">
        <button
          ref={mobileMenuTriggerRef}
          type="button"
          className="mobile-header-action"
          aria-label="Navigatiemenu openen"
          aria-expanded={mobilePanel === 'navigation'}
          aria-controls="mobile-navigation-panel"
          onClick={() => toggleMobilePanel('navigation')}
        >
          <span className="mobile-menu-icon" aria-hidden="true"><span /><span /><span /></span>
        </button>
        <span className="mobile-header-title">{currentPageLabel}</span>
        <button
          ref={mobileAccountTriggerRef}
          type="button"
          className="mobile-header-action"
          aria-label="Accountmenu openen"
          aria-expanded={mobilePanel === 'account'}
          aria-controls="mobile-account-panel"
          onClick={() => toggleMobilePanel('account')}
        >
          <Icon name="users" size={20} />
        </button>
      </div>

      {mobilePanel !== null && (
        <div className="mobile-panel-backdrop" onClick={() => setMobilePanel(null)} aria-hidden="true" />
      )}
      <nav
        id="mobile-navigation-panel"
        className={`mobile-panel mobile-navigation-panel ${mobilePanel === 'navigation' ? 'is-open' : ''}`}
        aria-label="Mobiele navigatie"
        aria-hidden={mobilePanel !== 'navigation'}
      >
        <div className="mobile-panel-header">
          <strong>Navigatie</strong>
          <button type="button" className="mobile-panel-close" tabIndex={mobilePanel === 'navigation' ? 0 : -1} onClick={() => setMobilePanel(null)} aria-label="Navigatiemenu sluiten">×</button>
        </div>
        <div className="mobile-navigation-list">
          {mobileNavItems.map((item) => (
            <NavLink
              key={`${item.key}-${item.path}`}
              to={item.path}
              className={`mobile-navigation-item ${item.active ? 'active' : ''} ${item.parentLabel ? 'is-subitem' : ''}`}
              onClick={handleSubmenuClick}
              tabIndex={mobilePanel === 'navigation' ? 0 : -1}
            >
              <span>{item.label}</span>
              {item.badgeCount > 0 && <span className="nav-badge" title="Nieuwe, ongelezen meldingsinformatie in deze vestiging" aria-label={item.badgeCount + ' ongelezen meldingen'}>{item.badgeCount}</span>}
            </NavLink>
          ))}
        </div>
      </nav>
      <aside
        id="mobile-account-panel"
        className={`mobile-panel mobile-account-panel ${mobilePanel === 'account' ? 'is-open' : ''}`}
        aria-label="Accountmenu"
        aria-hidden={mobilePanel !== 'account'}
      >
        <div className="mobile-panel-header">
          <strong>Account</strong>
          <button type="button" className="mobile-panel-close" tabIndex={mobilePanel === 'account' ? 0 : -1} onClick={() => setMobilePanel(null)} aria-label="Accountmenu sluiten">×</button>
        </div>
        <div className="mobile-account-actions">
          <div className="mobile-account-profile">
            <strong>{userProfile?.displayName || userProfile?.naam || user?.displayName || 'Account'}</strong>
            {user?.email && <span>{user.email}</span>}
          </div>
          {voiceSystem.voiceSupported && (
            <button type="button" tabIndex={mobilePanel === 'account' ? 0 : -1} className="mobile-account-action" onClick={voiceSystem.isListening ? stopVoiceRecognition : startVoiceRecognition}>
              {voiceSystem.isListening ? 'Luistert' : 'Spraak'}
            </button>
          )}
          <button type="button" tabIndex={mobilePanel === 'account' ? 0 : -1} className="mobile-account-action" onClick={onToggleDarkMode}>{darkMode ? 'Licht' : 'Donker'}</button>
          <button type="button" tabIndex={mobilePanel === 'account' ? 0 : -1} className="mobile-account-action mobile-account-action--logout" onClick={onLogout}>Uitloggen</button>
        </div>
      </aside>

      <div className="header-nav">
        <div className="nav-items">
          {navigationGroups.map((group) => (
            <div key={group.key} className={`nav-group nav-group--${group.key}`}>
              {group.items.map(renderDesktopNavItem)}
            </div>
          ))}
        </div>

        <div className="header-controls">
          <button
            type="button"
            className="control-btn notification-bell-btn"
            onClick={onToggleNotificationPopup}
            title="Notificaties bekijken"
            aria-pressed={showNotificationPopup}
            aria-label={`Notificaties bekijken${notificationCount > 0 ? `, ${visibleNotificationCount} ongelezen` : ''}`}
          >
            <Icon name="bell" size={18} className="nav-item-line-icon" />
            {notificationCount > 0 && <span className="nav-badge notification-badge">{visibleNotificationCount}</span>}
          </button>

          {voiceSystem.voiceSupported && (
            <button
              type="button"
              className={`control-btn voice-btn ${voiceSystem.isListening ? 'listening' : ''}`}
              onClick={voiceSystem.isListening ? stopVoiceRecognition : startVoiceRecognition}
              title={voiceSystem.response ? `Spraak assistent: ${voiceSystem.response}` : 'Spraak assistent'}
            >
              {voiceSystem.isListening ? 'Luistert' : 'Spraak'}
            </button>
          )}

          {belRoboticsLogo && (
            <a href="https://belrobotics.com/nl/" target="_blank" rel="noopener noreferrer" className="partner-logo-link" title="Belrobotics">
              <img src={belRoboticsLogo} alt="Belrobotics" className="partner-logo-small" />
            </a>
          )}

          <a href="https://www.husqvarna.com" target="_blank" rel="noopener noreferrer" className="partner-logo-link" title="Husqvarna">
            <img src={husqvarnaLogo} alt="Husqvarna" className="partner-logo-small partner-logo-husqvarna" />
          </a>

          <a href="https://www.kress.com" target="_blank" rel="noopener noreferrer" className="partner-logo-link" title="Kress">
            <img src={kressLogo} alt="Kress" className="partner-logo-small" />
          </a>

          <button type="button" className="control-btn theme-btn" onClick={onToggleDarkMode}>
            {darkMode ? 'Licht' : 'Donker'}
          </button>

          <button type="button" className="control-btn logout-btn" onClick={onLogout}>
            Uitloggen
          </button>
        </div>
      </div>
    </header>
  );
}
